import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Input } from "../components/UI";
import { PartyBadge } from "./CandidateDirectory";
import { toast } from "sonner";
import { Loader2, TrendingUp, TrendingDown, Minus, Search, Radio } from "lucide-react";

const MIcon = ({ d }) => d === "rising" ? <TrendingUp className="w-4 h-4 text-emerald-300" /> : d === "falling" ? <TrendingDown className="w-4 h-4 text-rose-300" /> : <Minus className="w-4 h-4 text-slate-400" />;

export default function OpponentIntel() {
  const [raceInput, setRaceInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (raceId) => {
    setLoading(true);
    try {
      const r = await api.get("/intelligence/opponents", { params: raceId ? { race_id: raceId } : {} });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Race not found — try e.g. 2026-S-GA");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Intelligence · Public sources only"
        title="Opponent Intelligence"
        subtitle="Share of voice, competitor momentum, and public activity for your race — auto-detected from your claimed candidate page."
      />

      <Card>
        <div className="flex gap-2 max-w-md">
          <Input value={raceInput} onChange={e => setRaceInput(e.target.value)} placeholder="Race ID e.g. 2026-S-GA or 2026-H-TX-21" data-testid="opp-race-input" />
          <Btn onClick={() => load(raceInput)} data-testid="opp-load-btn"><Search className="w-4 h-4" /> Load race</Btn>
        </div>
      </Card>

      {loading ? (
        <div className="py-12 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2 text-cyan-300" />Analyzing race…</div>
      ) : data && (
        <>
          <div className="text-sm text-slate-400">Tracking: <span className="text-white font-medium">{data.office_full} — {data.state}</span> <span className="text-slate-600">({data.race_id})</span></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="opp-sov-card">
              <h3 className="font-display text-lg font-bold mb-4">Share of voice (tracked support)</h3>
              <div className="space-y-3">
                {Object.entries(data.share_of_voice).map(([name, pct]) => {
                  const m = data.momentum.find(x => x.name === name);
                  return (
                    <div key={name}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <div className="flex items-center gap-2"><span className="text-white">{name}</span>{m && <PartyBadge party={m.party} />}</div>
                        <div className="flex items-center gap-2">{m && <MIcon d={m.direction} />}<span className="font-mono text-slate-300">{pct}%</span></div>
                      </div>
                      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: m?.party === "Republican" ? "#f87171" : m?.party === "Democratic" ? "#60a5fa" : "#fbbf24" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card padded={false} data-testid="opp-feed-card">
              <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
                <h3 className="font-display text-lg font-bold flex items-center gap-2"><Radio className="w-4 h-4 text-cyan-300" /> Public activity feed</h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-300 uppercase">Demo feed</span>
              </div>
              <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">
                {data.activity_feed.map((f, i) => (
                  <div key={i} className="px-5 py-3">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-white font-medium">{f.candidate}</span>
                      <PartyBadge party={f.party} />
                      <span className="ml-auto text-slate-600">{f.date}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1"><span className="text-cyan-300">{f.kind}:</span> {f.summary}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
